import { apiFetch } from '../services/api';
import { Icon } from './icons/Icon';
/**
 * TripleSplitPlayer — トリプル・スプリット再生画面
 *
 * v3ガイドライン「フェーズD：三位一体の共創と文化形成」の中核コンポーネント。
 * 台本×絵×声を対等な主役として表示し、ピア・レコグニション（ピンポイント称賛）
 * と自己拡張（系譜ツリー）のUIを統合する。
 *
 * 設計原則:
 * ① カルチャー: トリプル・クレジットで3者を対等表示 / 再生数は非表示
 * ② ゲームニクス: 各セクション4アクション以内 / 点滅誘導 / 全操作にSE
 * ③ 行動心理学: 称賛の予測不可能性（可変報酬） / 系譜の未完了タスク
 *
 * @see docs/GAMENICS_DESIGN_GUIDELINES.md フェーズD
 */

import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import { WaveformPlayer } from './WaveformPlayer';
import { PulseButton } from './ui/PulseButton';
import { SubmissionModal } from './SubmissionModal';
import { COPY, ROLE_LABELS, INCOMPLETE_TASKS, type RoleType } from '../lib/copyDictionary';
import { useGamenics, GamenicsEvent } from '../hooks/useGamenics';
import clsx from 'clsx';
import { LineageViewer } from './LineageViewer';
import { ScriptViewer } from './ScriptViewer';
import { IllustrationViewer } from './IllustrationViewer';

// ────────────────────────────────────────────
// Types
// ────────────────────────────────────────────

interface Creator {
  id: string;
  displayName: string;
  iconUrl: string | null;
  role: RoleType;
  praiseCount: number;
}

interface PinpointPraise {
  id: string;
  target_type: 'audio' | 'image' | 'script';
  target_position: string | null;
  timestamp_ms: number | null;
  timestamp_end_ms: number | null;
  praise_text: string;
  is_ai_generated: number;
  ai_star_json: string | null;
  user_id: string;
  display_name: string;
  icon_url: string | null;
  created_at: number;
}

interface LineageNode {
  work_id: string;
  parent_work_id: string | null;
  work_type: RoleType;
  creator_id: string;
  title: string | null;
  display_name: string;
  depth: number;
}

interface TripleSplitPlayerProps {
  workId: string;
  submissionId: string;
  audioUrl?: string;
  imageUrl?: string;
  textContent?: string;
  title: string;
  isLoggedIn?: boolean;
  onClose?: () => void;
}

// ────────────────────────────────────────────
// Role Color Utilities
// ────────────────────────────────────────────

const ROLE_COLORS: Record<RoleType, string> = {
  script: '#f59e0b',        // Amber
  illustration: '#8b5cf6',  // Violet
  voice: '#06b6a4',         // Cyan
};

const ROLE_ACCENT_CLASSES: Record<RoleType, string> = {
  script: 'border-t-role-script',
  illustration: 'border-t-role-illustration',
  voice: 'border-t-role-voice',
};

// ────────────────────────────────────────────
// Component
// ────────────────────────────────────────────

export const TripleSplitPlayer: FC<TripleSplitPlayerProps> = ({
  workId,
  submissionId,
  audioUrl,
  imageUrl,
  textContent,
  title,
  isLoggedIn = false,
  onClose,
}) => {
  const { playSound, triggerCelebration } = useGamenics();

  // ── State ──
  const [creators, setCreators] = useState<Creator[]>([]);
  const [praises, setPraises] = useState<PinpointPraise[]>([]);
  const [lineage, setLineage] = useState<LineageNode[]>([]);
  const [showPraiseInput, setShowPraiseInput] = useState(false);
  const [showLineageViewer, setShowLineageViewer] = useState(false);
  const [praiseTargetType, setPraiseTargetType] = useState<'audio'|'image'|'script'>('audio');
  const [praiseTargetPosition, setPraiseTargetPosition] = useState<string>('0');
  const [praiseText, setPraiseText] = useState('');
  const [expandedStar, setExpandedStar] = useState<string | null>(null);
  const [showMore, setShowMore] = useState(false);
  
  // モーダルステート
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalRole, setModalRole] = useState<'script' | 'illustration' | 'voice'>('voice');
  
  // ── AI Praise State ──
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<{ emotional: { text: string }; technical: { text: string }; character: { text: string } } | null>(null);
  const [slotText, setSlotText] = useState('思考中...');
  const [selectedAiJson, setSelectedAiJson] = useState<string | null>(null);

  const [isFirstVisit] = useState(() => {
    const key = `triple_split_visited_${workId}`;
    const visited = localStorage.getItem(key);
    if (!visited) localStorage.setItem(key, '1');
    return !visited;
  });

  const praiseInputRef = useRef<HTMLTextAreaElement>(null);

  // ── Data Loading ──
  useEffect(() => {
    loadData();
  }, [workId, submissionId]);

  const loadData = async () => {

    // クレジット
    try {
      const creditRes = await apiFetch(`/api/user/credits/${workId}`);
      const creditData = await creditRes.json();
      if (creditData.success && creditData.data) {
        const c = creditData.data;
        const crs: Creator[] = [];
        if (c.script_creator_id) {
          crs.push({
            id: c.script_creator_id,
            displayName: c.script_name || '—',
            iconUrl: c.script_icon,
            role: 'script',
            praiseCount: 0,
          });
        }
        if (c.illustration_creator_id) {
          crs.push({
            id: c.illustration_creator_id,
            displayName: c.illustration_name || '—',
            iconUrl: c.illustration_icon,
            role: 'illustration',
            praiseCount: 0,
          });
        }
        if (c.voice_creator_id) {
          crs.push({
            id: c.voice_creator_id,
            displayName: c.voice_name || '—',
            iconUrl: c.voice_icon,
            role: 'voice',
            praiseCount: 0,
          });
        }
        setCreators(crs);
      }
    } catch {
      // 【フェイルセーフ】クレジットデータなし → フォールバック表示
    }

    // フォールバック: クレジットがなくても作品の制作者を表示
    if (creators.length === 0) {
      setCreators([
        { id: '', displayName: '—', iconUrl: null, role: 'script', praiseCount: 0 },
        { id: '', displayName: '—', iconUrl: null, role: 'illustration', praiseCount: 0 },
        { id: '', displayName: '参加者', iconUrl: null, role: 'voice', praiseCount: 0 },
      ]);
    }

    // ピンポイント称賛
    try {
      const praiseRes = await apiFetch(`/api/user/praises/${submissionId}`);
      const praiseData = await praiseRes.json();
      if (praiseData.success) setPraises(praiseData.data || []);
    } catch {
      // 【フェイルセーフ】称賛データなし → 空配列
    }

    // リネージュ
    let lineageData: any = null;
    try {
      const lineageRes = await apiFetch(`/api/public/lineage/${workId}`);
      lineageData = await lineageRes.json();
      if (lineageData.success) {
        const lNodes = lineageData.data || [];
        setLineage(lNodes);

        // 【追加フォールバック】クレジットが取得できなかった場合、リネージュから抽出を試みる
        setCreators(prev => {
          // 既に有効なデータが入っていれば何もしない
          if (prev.length > 0 && prev.some(c => c.id)) return prev;

          const crs: Creator[] = [
            { id: '', displayName: '—', iconUrl: null, role: 'script', praiseCount: 0 },
            { id: '', displayName: '—', iconUrl: null, role: 'illustration', praiseCount: 0 },
            { id: '', displayName: '参加者', iconUrl: null, role: 'voice', praiseCount: 0 },
          ];

          lNodes.forEach((node: any) => {
            const roleIdx = node.work_type === 'script' ? 0 : node.work_type === 'illustration' ? 1 : node.work_type === 'voice' ? 2 : -1;
            if (roleIdx !== -1) {
              crs[roleIdx] = {
                id: node.creator_id,
                displayName: node.display_name || '—',
                iconUrl: node.icon_url || null,
                role: node.work_type,
                praiseCount: 0
              };
            }
          });
          return crs;
        });
      }
    } catch {
      // 【フェイルセーフ】リネージュデータなし → 空配列
    }
  };

  // ── Praise Submission ──
  const handlePraiseSend = useCallback(async () => {
    if (!praiseText.trim() || !isLoggedIn) return;

    try {

      const res = await apiFetch('/api/user/praises', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          submission_id: submissionId,
          target_type: praiseTargetType,
          target_position: praiseTargetPosition,
          timestamp_ms: praiseTargetType === 'audio' ? parseInt(praiseTargetPosition, 10) : null,
          praise_text: praiseText.trim(),
          is_ai_generated: selectedAiJson ? 1 : 0,
          ai_star_json: selectedAiJson ? JSON.parse(selectedAiJson) : null,
        }),
      });
      const data = await res.json();
      console.log('[TripleSplit] Praise send response:', data);

      if (data.success) {
        // 【間】設定値のフリーズ → SE → 演出 (gamenics_effects の delay_before_ms を利用)
        await triggerCelebration(GamenicsEvent.LIKE_RECEIVED);

        // 称賛リストに追加
        setPraises(prev => [
          ...prev,
          {
            id: data.data.id || Math.random().toString(),
            target_type: praiseTargetType,
            target_position: praiseTargetPosition,
            timestamp_ms: praiseTargetType === 'audio' ? parseInt(praiseTargetPosition, 10) : null,
            timestamp_end_ms: null,
            praise_text: praiseText.trim(),
            is_ai_generated: selectedAiJson ? 1 : 0,
            ai_star_json: selectedAiJson,
            user_id: '',
            display_name: 'あなた',
            icon_url: null,
            created_at: Date.now(),
          },
        ].sort((a, b) => (a.timestamp_ms || 0) - (b.timestamp_ms || 0)));

        setPraiseText('');
        setSelectedAiJson(null);
        setAiSuggestions(null);
        setShowPraiseInput(false);
      }
    } catch (e) {
      console.warn('[TripleSplit] Praise send failed:', e);
    }
  }, [praiseText, submissionId, praiseTargetType, praiseTargetPosition, isLoggedIn, triggerCelebration, selectedAiJson]);

  // ── AI Praise Slot Animation ──
  useEffect(() => {
    if (!aiLoading) return;
    const words = ["感情を分析しています", "熱量を感じ取っています", "息遣いを言語化しています", "表現を探しています"];
    let i = 0;
    playSound(GamenicsEvent.TAP_CONFIRM); // 開始音
    const interval = setInterval(() => {
      setSlotText(words[i % words.length] + '...');
      i++;
    }, 400); // カシャカシャ入れ替わる演出
    return () => clearInterval(interval);
  }, [aiLoading, playSound]);

  // ── Request AI Praise ──
  const handleAiPraiseRequest = async () => {
    setAiLoading(true);
    setAiSuggestions(null);
    setSelectedAiJson(null);
    try {

      const res = await apiFetch('/api/user/praise/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          workId, 
          timestamp_ms: praiseTargetType === 'audio' ? parseInt(praiseTargetPosition, 10) : 0,
          target_type: praiseTargetType,
          target_position: praiseTargetPosition
        })
      });
      const data = await res.json();
      if (data.success && data.data) {
        playSound(GamenicsEvent.SUBMISSION_COMPLETE);
        setAiSuggestions(data.data);
      }
    } catch (e) {
      console.warn('AI Praise failed', e);
    } finally {
      setAiLoading(false);
    }
  };

  // ── Helpers ──
  const formatTime = (ms: number): string => {
    const totalSec = Math.floor(ms / 1000);
    const mins = Math.floor(totalSec / 60);
    const secs = totalSec % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const missingRoles = (['script', 'illustration', 'voice'] as RoleType[])
    .filter(role => !creators.find(c => c.role === role && c.id));

  // ────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex flex-col pointer-events-none">
      {/* 背景（アニメーション付き） */}
      <div className="absolute inset-0 bg-ink/80 backdrop-blur-sm pointer-events-auto animate-slide-up" />

      {/* ── メインスクロール領域 ── */}
      <div className="relative z-10 flex-1 overflow-y-auto pointer-events-auto animate-slide-up">
        <div className="max-w-2xl mx-auto px-4 py-6 pb-32">

        {/* ── ヘッダー (2アクション) ── */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => {
              playSound(GamenicsEvent.TAP_CANCEL);
              onClose?.();
            }}
            className="text-ink-2 hover:text-ink transition-colors text-sm flex items-center gap-1"
          >
            ← {COPY.back}
          </button>
          <button
            onClick={() => {
              playSound(GamenicsEvent.TAP_CONFIRM);
              setShowMore(!showMore);
            }}
            className="text-ink-2 hover:text-ink transition-colors text-sm"
          >
            {COPY.more}
          </button>
        </div>

        {/* ⋯もっとメニュー（4つ以内） */}
        {showMore && (
          <div className="mb-4 p-3 bg-cream rounded-xl border border-ink/15 flex gap-2 flex-wrap animate-stagger-in">
            <PulseButton size="sm" variant="ghost" onClick={() => playSound(GamenicsEvent.TAP_CONFIRM)}>{COPY.like}</PulseButton>
            <PulseButton size="sm" variant="ghost" onClick={() => playSound(GamenicsEvent.TAP_CONFIRM)}>{COPY.gift}</PulseButton>
            <PulseButton size="sm" variant="ghost" onClick={() => playSound(GamenicsEvent.TAP_CONFIRM)}>{COPY.share}</PulseButton>
            <PulseButton size="sm" variant="ghost" onClick={() => playSound(GamenicsEvent.TAP_CONFIRM)}>{COPY.embed}</PulseButton>
          </div>
        )}

        {/* ── トリプル・クレジットヘッダー ── */}
        <div className="grid grid-cols-3 gap-2 mb-6">
          {(['script', 'illustration', 'voice'] as RoleType[]).map((role, i) => {
            const creator = creators.find(c => c.role === role);
            return (
              <div
                key={role}
                className={clsx(
                  'relative bg-cream rounded-xl p-3 text-center border-t-2 transition-all',
                  ROLE_ACCENT_CLASSES[role],
                  'animate-stagger-in',
                )}
                style={{ animationDelay: `${i * 100}ms` }}
              >
                {/* アイコン */}
                <div className="w-10 h-10 mx-auto mb-2 rounded-full overflow-hidden bg-cream-2">
                  {creator?.iconUrl ? (
                    <img src={creator.iconUrl} alt={creator.displayName} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-lg opacity-50">
                      {ROLE_LABELS[role].charAt(0)}
                    </div>
                  )}
                </div>

                {/* ロールラベル */}
                <div className="text-xs font-medium mb-1" style={{ color: ROLE_COLORS[role] }}>
                  {ROLE_LABELS[role]}
                </div>

                {/* クリエイター名 */}
                <div className="text-xs text-ink truncate">
                  {creator?.id ? `@${creator.displayName}` : (
                    <span className="text-ink-3 italic">
                      {creator?.displayName || '参加者募集中'}
                    </span>
                  )}
                </div>

                {/* 称賛数 */}
                {creator?.praiseCount ? (
                  <div className="text-xs text-ink-2 mt-1">★ {creator.praiseCount}件</div>
                ) : null}
              </div>
            );
          })}
        </div>

        {/* ── 作品タイトル ── */}
        <h2 className="text-xl font-display font-bold text-ink mb-4 text-center">
          {title}
        </h2>

        {/* ── ウェーブフォームプレーヤー ── */}
        {audioUrl && (
          <div className="mb-6">
            <h3 className="text-sm font-bold text-ink-2 mb-2"><Icon name="voice" size={14} /> 音声 (タップして称賛)</h3>
            <WaveformPlayer
              audioUrl={audioUrl}
              title={title}
              authorName={creators.find(c => c.role === 'voice')?.displayName || 'クリエイター'}
              imageUrl={imageUrl}
              comments={praises.filter(p => p.target_type === 'audio').map(p => ({
                id: p.id,
                timestampSec: (p.timestamp_ms || 0) / 1000,
                content: p.praise_text,
                createdAt: p.created_at,
                user: {
                  id: p.user_id,
                  displayName: p.display_name,
                  iconUrl: p.icon_url || '',
                },
              }))}
              isLoggedIn={isLoggedIn}
              onAddComment={(ts) => {
                playSound(GamenicsEvent.TAP_CONFIRM);
                setPraiseTargetType('audio');
                setPraiseTargetPosition((ts * 1000).toString()); // sec → ms
                setShowPraiseInput(true);
                setTimeout(() => praiseInputRef.current?.focus(), 100);
              }}
            />
          </div>
        )}

        {/* ── イラストビューアー ── */}
        {imageUrl && (
          <div className="mb-6">
            <h3 className="text-sm font-bold text-ink-2 mb-2"><Icon name="art" size={14} /> 絵 (タップして称賛)</h3>
            <IllustrationViewer
              imageUrl={imageUrl}
              comments={praises.filter(p => p.target_type === 'image').map(p => ({
                id: p.id,
                position: p.target_position ? JSON.parse(p.target_position) : { x: 50, y: 50 },
                content: p.praise_text,
                createdAt: p.created_at,
                user: { id: p.user_id, displayName: p.display_name, iconUrl: p.icon_url || null }
              }))}
              isLoggedIn={isLoggedIn}
              onAddComment={(x, y) => {
                setPraiseTargetType('image');
                setPraiseTargetPosition(JSON.stringify({ x, y }));
                setShowPraiseInput(true);
                setTimeout(() => praiseInputRef.current?.focus(), 100);
              }}
            />
          </div>
        )}

        {/* ── スクリプトビューアー ── */}
        {textContent && (
          <div className="mb-6">
            <h3 className="text-sm font-bold text-ink-2 mb-2"><Icon name="script" size={14} /> 台本 (行をタップして称賛)</h3>
            <ScriptViewer
              textContent={textContent}
              comments={praises.filter(p => p.target_type === 'script').map(p => ({
                id: p.id,
                lineNumber: p.target_position ? parseInt(p.target_position, 10) : 0,
                content: p.praise_text,
                createdAt: p.created_at,
                user: { id: p.user_id, displayName: p.display_name, iconUrl: p.icon_url || null }
              }))}
              isLoggedIn={isLoggedIn}
              onAddComment={(lineNumber) => {
                setPraiseTargetType('script');
                setPraiseTargetPosition(lineNumber.toString());
                setShowPraiseInput(true);
                setTimeout(() => praiseInputRef.current?.focus(), 100);
              }}
            />
          </div>
        )}

        {/* ── ピンポイント称賛入力 ── */}
        {showPraiseInput && (
          <div className="mb-6 p-4 bg-dark-surface rounded-xl border border-primary/30 shadow-[0_0_20px_rgba(0,243,255,0.1)] animate-slide-up">
            <div className="flex justify-between items-center mb-3">
              <p className="text-sm font-bold text-ink">
                {praiseTargetType === 'audio'
                  ? `${formatTime(parseInt(praiseTargetPosition, 10))} に言葉を贈る`
                  : praiseTargetType === 'script'
                  ? `${parseInt(praiseTargetPosition, 10) + 1}行目に言葉を贈る`
                  : `絵の指定箇所に言葉を贈る`
                }
              </p>
              {!aiLoading && !aiSuggestions && (
                <PulseButton
                  variant="ghost"
                  size="sm"
                  onClick={handleAiPraiseRequest}
                  className="text-xs font-bold text-primary border border-primary/50 px-3 py-1 rounded-full animate-marker-pulse hover:bg-primary/10 flex items-center gap-1"
                >
                  {COPY.praiseWithAI}
                </PulseButton>
              )}
            </div>

            {/* AI ローディングスロット */}
            {aiLoading && (
              <div className="py-6 text-center text-primary font-mono text-sm animate-pulse flex flex-col items-center gap-2">
                <div className="w-6 h-6 border-2 border-t-primary border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
                {slotText}
              </div>
            )}

            {/* AI 提案カード（3つ・自己選択） */}
            {aiSuggestions && !selectedAiJson && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
                {(Object.entries(aiSuggestions) as [string, {text: string}][]).map(([key, val], i) => (
                  <PulseButton
                    key={key}
                    variant="ghost"
                    className="p-3 h-auto bg-cream hover:bg-primary/10 border border-ink/15 hover:border-primary/50 rounded-lg text-left text-xs transition-all animate-stagger-in hover:-translate-y-1 block w-full whitespace-normal"
                    style={{ animationDelay: `${i * 150}ms` }}
                    onClick={() => {
                      playSound(GamenicsEvent.TAP_CONFIRM);
                      setPraiseText(val.text);
                      // トーンとテキストをJSONとして保存
                      setSelectedAiJson(JSON.stringify({
                        situation: 'この瞬間において',
                        task: '表現の効果',
                        action: val.text,
                        result: '心が動かされた',
                        tone: key
                      }));
                      setTimeout(() => praiseInputRef.current?.focus(), 100);
                    }}
                  >
                    <div className="font-bold text-primary mb-1 capitalize">
                      {key === 'emotional' ? 'エモーショナル' : key === 'technical' ? 'テクニカル' : 'キャラクター'}
                    </div>
                    <div className="text-ink-2 leading-relaxed">{val.text}</div>
                  </PulseButton>
                ))}
              </div>
            )}

            <div className="flex flex-col gap-3">
              <textarea
                ref={praiseInputRef}
                value={praiseText}
                onChange={(e) => setPraiseText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handlePraiseSend();
                  }
                }}
                placeholder={aiSuggestions ? "さらにあなたの言葉（魂）を足してください..." : "「ここのブレスが最高」..."}
                maxLength={400}
                rows={3}
                className="w-full bg-cream text-ink px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none placeholder-gray-500"
              />
              
              <div className="flex justify-between items-center">
                <PulseButton
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    playSound(GamenicsEvent.TAP_CANCEL);
                    setShowPraiseInput(false);
                    setAiSuggestions(null);
                    setSelectedAiJson(null);
                  }}
                  className="text-xs text-ink-3 hover:text-ink-2"
                >
                  {COPY.cancel}
                </PulseButton>
                <PulseButton
                  size="md"
                  isPrimary={praiseText.trim().length > 0}
                  onClick={handlePraiseSend}
                  disabled={!praiseText.trim()}
                >
                  {selectedAiJson ? '届ける！' : COPY.submit}
                </PulseButton>
              </div>
            </div>
          </div>
        )}

        {/* ── ピンポイント称賛パネル ── */}
        <div className="mb-6">
          <h3 className="text-sm font-bold text-ink-2 mb-3 flex items-center gap-2">
            ピンポイント称賛
            <span className="text-xs text-ink-3 font-normal">({praises.length}件)</span>
          </h3>

          {praises.length === 0 ? (
            <div className="text-center py-6 bg-cream rounded-xl border border-dashed border-ink/15">
              <p className="text-ink-3 text-sm mb-2">まだ称賛がありません</p>
              {isLoggedIn && (
                <p className="text-ink-3 text-xs">波形をタップして最初の称賛を残そう</p>
              )}
            </div>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {praises.map((p) => (
                <div
                  key={p.id}
                  className="flex items-start gap-2.5 p-3 bg-cream rounded-lg hover:bg-cream-2 transition-colors cursor-pointer"
                  onClick={() => {
                    if (p.is_ai_generated && p.ai_star_json) {
                      setExpandedStar(expandedStar === p.id ? null : p.id);
                    }
                  }}
                >
                  {/* アバター */}
                  <div className="w-6 h-6 rounded-full bg-cream-2 overflow-hidden flex-shrink-0 mt-0.5">
                    {p.icon_url ? (
                      <img src={p.icon_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-ink-3"><Icon name="user" size={12} className="text-ink-3" /></div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs mb-1">
                      <span className="text-ink-2">{p.display_name}</span>
                      <span className="text-role-voice font-mono">{formatTime(p.timestamp_ms || 0)}</span>
                      {p.is_ai_generated ? (
                        <span className="bg-role-voice/20 text-role-voice px-1.5 py-0.5 rounded text-[10px] font-bold">
                          STAR
                        </span>
                      ) : null}
                    </div>
                    <p className="text-ink text-sm">{p.praise_text}</p>

                    {/* STAR 法展開 */}
                    {expandedStar === p.id && p.ai_star_json && (() => {
                      try {
                        const star = JSON.parse(p.ai_star_json);
                        return (
                          <div className="mt-2 p-2.5 bg-cream-2 rounded-lg border border-role-voice/20 text-xs space-y-1 animate-stagger-in">
                            <div><span className="text-role-script font-bold">【状況】</span> {star.situation}</div>
                            <div><span className="text-role-illustration font-bold">【課題】</span> {star.task}</div>
                            <div><span className="text-role-voice font-bold">【行動】</span> {star.action}</div>
                            <div><span className="text-role-voice font-bold">【結果】</span> {star.result}</div>
                          </div>
                        );
                      } catch { return null; }
                    })()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── リネージュ・ミニマップ ── */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-ink-2 flex items-center gap-2">
              系譜ツリー
            </h3>
            {lineage.length > 0 && (
              <PulseButton
                variant="ghost"
                size="sm"
                onClick={() => {
                  playSound(GamenicsEvent.TAP_CONFIRM);
                  setShowLineageViewer(true);
                }}
                className="text-xs text-primary hover:text-ink transition-colors flex items-center gap-1"
              >
                {COPY.viewLineage} ↗
              </PulseButton>
            )}
          </div>

          {lineage.length === 0 ? (
            <div className="text-center py-4 bg-cream rounded-xl border border-dashed border-ink/15">
              <p className="text-ink-3 text-sm">まだ系譜がありません</p>
              <p className="text-ink-3 text-xs mt-1">この作品が最初の起点です</p>
            </div>
          ) : (
            <div className="flex items-center gap-1 overflow-x-auto p-3 bg-cream rounded-xl">
              {lineage.slice(0, 8).map((node, i) => (
                <React.Fragment key={node.work_id}>
                  {i > 0 && <span className="text-ink-3 text-xs">→</span>}
                  <div
                    className={clsx(
                      'flex-shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-medium border',
                      node.work_id === workId
                        ? 'bg-cream-2 border-ink text-ink'
                        : 'bg-cream border-ink/15 text-ink-2',
                    )}
                    style={{ borderLeftColor: ROLE_COLORS[node.work_type], borderLeftWidth: '3px' }}
                  >
                    <span>{ROLE_LABELS[node.work_type].charAt(0)}</span>
                    <span className="ml-1">{node.display_name || '—'}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}

          {/* 未完了タスク（ツァイガルニク効果） */}
          {missingRoles.length > 0 && (
            <div className="mt-2 text-xs text-ink-3 italic">
              {INCOMPLETE_TASKS.lineageMissing(missingRoles[0])}
            </div>
          )}
        </div>
      </div>
      </div>

      {/* ── アクションバー（3つ以内 + ⋯） ── */}
      <div className="absolute bottom-0 left-0 right-0 bg-dark-bg/95 backdrop-blur border-t border-ink/15 p-4 z-20 pointer-events-auto animate-slide-up">
        <div className="max-w-2xl mx-auto flex items-center justify-center gap-3">
          <PulseButton
            isPrimary={isFirstVisit && isLoggedIn}
            size="md"
            onClick={() => {
              playSound(GamenicsEvent.TAP_CONFIRM);
              setPraiseTargetType('audio');
              setPraiseTargetPosition('0');
              setShowPraiseInput(true);
              setTimeout(() => {
                praiseInputRef.current?.focus();
                // スクロールを上部へ（アクションバーとの干渉を防ぐ）
                // スクロールコンテナが body ではなくなったため、専用の仕組みが必要であれば実装可能ですが
                // ここでは不要とします。
              }, 100);
            }}
            disabled={!isLoggedIn}
          >
            {COPY.praise}
          </PulseButton>

          <PulseButton
            size="md"
            variant="outline"
            roleColor="voice"
            onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setModalRole('voice'); setIsModalOpen(true); }}
            disabled={!isLoggedIn}
          >
            {COPY.addVoice}
          </PulseButton>

          {missingRoles.includes('illustration') && (
            <PulseButton
              size="md"
              variant="outline"
              roleColor="illustration"
              isPrimary={missingRoles.includes('illustration')}
              onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setModalRole('illustration'); setIsModalOpen(true); }}
              disabled={!isLoggedIn}
            >
              {COPY.addIllustration}
            </PulseButton>
          )}
        </div>
      </div>

      {/* フルスクリーン リネージュビューア */}
      {showLineageViewer && (
        <LineageViewer
          nodes={lineage}
          currentWorkId={workId}
          onClose={() => setShowLineageViewer(false)}
        />
      )}

      {/* 投稿モーダル */}
      {isModalOpen && (
        <SubmissionModal
          postId={workId}
          role={modalRole}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => {
            setIsModalOpen(false);
            // 投稿後にリフレッシュする処理（親から渡されたり、リロードするなど）
            window.location.reload();
          }}
        />
      )}
    </div>
  );
};
